import { apiClient } from "@/api/api-client";
import { useAuth } from "@/hooks/use-auth";
import ReactECharts from "echarts-for-react";
import { animate } from "framer-motion";
import { size } from "lodash";
import { s } from "node_modules/framer-motion/dist/types.d-O7VGXDJe";
import { useEffect, useRef, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Label, LabelList, Pie, PieChart, ResponsiveContainer, Sector, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "../core/buttons/button";

type CaseData = {
	case_category_name: string;
	pending_count: number;
	total_count: number;
	with_decision_count: number;
};

const CasesPieChart = ({ hasBranch = false }: { hasBranch: boolean }) => {
	const myChart = useRef<any>(null);
	const COLORS = [
		"#ec4899",
		"#d946ef",
		"#8b5cf6",
		"#6366f1",
		"#3b82f6",
		"#06b6d4",
		"#0ea5e9",
		"#22c55e",
		"#84cc16",
		"#a3e635",
		"#facc15",
		"#fbbf24",
		"#f59e0b",
		"#d97706",
		"#b45309",
	];

	const { user } = useAuth();

	const [options, setOptions] = useState<any>({
		tooltip: {
			trigger: "item",
		},
		legend: {
			orient: "horizontal",
			top: 20,
			textStyle: {
				fontSize: 10,
			},
		},
		series: [],
	});

	const fetchStatistics = async () => {
		const ids = hasBranch ? user?.branches?.map((branch) => branch?.assignable?.id) : "";
		const response = await apiClient.get(`/backoffice/province-case-report?branch_id=${ids}`);
		const x = response.data.map((data: CaseData, idx: number) => {
			return {
				name: data.case_category_name,
				value: data.total_count,
			};
		});

		setOptions((prevState) => {
			return {
				...prevState,
				series: response.data.map((data, idx) => {
					return {
						type: "pie",
						radius: "60%",
						top: 40,
						height: "100%",
						left: "center",
						width: "90%",
						avoidLabelOverlap: true,
						itemStyle: {
							borderColor: "#fff",
							borderWidth: 1,
						},
						label: {
							alignTo: "edge",
							formatter: "{name|{b}}\n{total|Total Cases: {c} ({d}%)}",
							minMargin: 5,
							edgeDistance: 10,
							lineHeight: 15,
							rich: {
								total: {
									fontSize: 10,
									color: "#999",
								},
							},
						},
						labelLine: {
							length: 15,
							length2: 0,
							maxSurfaceAngle: 80,
						},
						labelLayout: (params) => {
							const isLeft = params.labelRect.x < myChart.current.offsetWidth / 2;
							const points = params.labelLinePoints;
							// Update the end point.
							points[2][0] = isLeft ? params.labelRect.x : params.labelRect.x + params.labelRect.width;
							return {
								labelLinePoints: points,
							};
						},
						data: x,
					};
				}),
			};
		});
	};

	useEffect(() => {
		fetchStatistics();
	}, []);

	return (
		<div className="grid grid-cols-12 w-full gap-4">
			<div className="col-span-12">
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full h-auto rounded-md border border-border/50 overflow-hidden">
						<div className="flex flex-row w-full p-3 border-b border-border/50 items-center">
							<div className="flex flex-col w-6/12 text-base font-semibold text-foreground-dark">Cases Summary</div>
						</div>
						<div className="w-full h-full">
							<ReactECharts option={options} style={{ height: "600px", width: "100%" }} ref={myChart} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CasesPieChart;
